'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { getCreatorLoginUrl, getCreatorRegisterUrl } from '@/lib/creatorPlatformUrls';
import LoadingSpinner from '@/components/LoadingSpinner';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';

const VIEWPORT_CLASS = 'in-view';
const IO_OPTIONS: IntersectionObserverInit = {
  root: null,
  rootMargin: '0px 0px -8% 0px',
  threshold: 0.05,
};

const HERO_VIDEO_SRC = '/home/hero-bg.mp4';
const HERO_VIDEO_POSTER = '/home/hero-poster.jpg';

const BLOCKS: Array<{
  id: string;
  imageSlug: string;
  title: string;
  tagline: string;
  text: string;
  alt: string;
}> = [
  {
    id: 'distribution',
    imageSlug: 'distribution',
    title: '全球发行',
    tagline: '作品同步全球，直达更多听众',
    text: '一键提交，即可发布至 Spotify、Apple Music、QQ 音乐、网易云等 30+ 主流音乐平台，让你的音乐被更多人听见。',
    alt: '全球发行',
  },
  {
    id: 'aiBroker',
    imageSlug: 'ai-broker',
    title: 'AI 经纪人',
    tagline: '有问即答，专业陪跑',
    text: '商务咨询、发行流程、平台规则，到数据解读与策略建议，专属 AI 经纪人 24 小时在线解答。你只需专注创作，我们陪伴你的每一步成长。',
    alt: 'AI 经纪人',
  },
  {
    id: 'data',
    imageSlug: 'data',
    title: '数据统计',
    tagline: '一站整合，数据一目了然',
    text: '多平台播放、粉丝与收益数据自动汇总，清晰看懂每一首作品的真实表现，为你的创作与发行决策提供有力支持。',
    alt: '数据统计',
  },
  {
    id: 'revenue',
    imageSlug: 'revenue',
    title: '收益变现',
    tagline: '清晰透明，安心到账',
    text: '海内外收益自动归集，明细可查、进度可追踪，支持多币种与合规提现，到账清晰可预期。',
    alt: '收益与提现',
  },
];

export default function HomePageClient() {
  const router = useRouter();
  const { user, loading } = useAuth();
  const pageRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);
  const sectionRefs = useRef<(HTMLElement | null)[]>([]);
  const ctaSectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (user && !loading) {
      router.replace('/dashboard');
    }
  }, [user, loading, router]);

  /* 首页挂载时锁定 body/html 不滚动，仅 .home-page 内滚动，保证 scroll-snap 生效 */
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add('home-page-active');
    body.classList.add('home-page-active');
    return () => {
      root.classList.remove('home-page-active');
      body.classList.remove('home-page-active');
    };
  }, []);

  useEffect(() => {
    if (user) return;
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      el.style.setProperty('--mouse-x', `${x}%`);
      el.style.setProperty('--mouse-y', `${y}%`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, [user]);

  /* 首屏 poster 高优先级预加载，保证 LCP 稳定 */
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = HERO_VIDEO_POSTER;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
  }, []);

  /* 首屏即有 source；可播时试 play，被策略拦截则等首次触摸/点击后再 play；绑定前若已可播则立即试播 */
  useEffect(() => {
    if (user) return;
    const video = heroVideoRef.current;
    if (!video) return;
    video.muted = true;
    const tryPlayWithFallback = () => {
      if (!video.paused) return;
      video.muted = true;
      video.play().catch(() => {
        const tryPlay = (e: Event) => {
          const root = document.getElementById('drawer-portal-root');
          if (root?.contains(e.target as Node)) return;
          heroVideoRef.current?.play().catch(() => {});
        };
        document.addEventListener('touchstart', tryPlay, { once: true, passive: true });
        document.addEventListener('click', tryPlay, { once: true });
      });
    };
    if (video.readyState >= 3) {
      tryPlayWithFallback();
      return;
    }
    video.addEventListener('canplay', tryPlayWithFallback, { once: true });
    return () => video.removeEventListener('canplay', tryPlayWithFallback);
  }, [user]);

  /* 顶栏三态：顶部透明、即将压到 hero 文字时隐藏、进入全球发行后白底；scroll/IO/首帧 + 近顶延迟同步，避免停在顶部无事件导致白底 */
  useEffect(() => {
    const container = pageRef.current;
    const hero = heroRef.current;
    const header = document.getElementById('home-header');
    if (!container || !hero || !header) return;

    /* 与 scroll-snap 一致：snap 到 hero 时 scrollTop 常为顶栏高度(约 71)，故阈值需 ≥ 72 才在“视觉顶部”显示透明 */
    const SCROLL_TOP_TRANSPARENT = 80;
    const SCROLL_TOP_NEAR = 100;
    const OVERLAP_BUFFER = 8;
    const SYNC_AFTER_STOP_MS = 120;

    function updateHeaderState() {
      const c = pageRef.current;
      const h = heroRef.current;
      const head = document.getElementById('home-header');
      if (!c || !h || !head) return;
      const scrollTop = c.scrollTop;
      const heroContent = h.querySelector<HTMLElement>('.home-hero-content');
      const contentTop = heroContent
        ? h.offsetTop + heroContent.offsetTop - scrollTop
        : h.offsetTop - scrollTop;
      const headerHeight = head.offsetHeight;
      const pastHero = scrollTop >= h.offsetTop + h.offsetHeight;
      const inOverlapZone = contentTop <= headerHeight + OVERLAP_BUFFER;

      if (scrollTop <= SCROLL_TOP_TRANSPARENT) {
        head.classList.remove('is-scrolled', 'is-hidden');
      } else if (pastHero) {
        head.classList.remove('is-hidden');
        head.classList.add('is-scrolled');
      } else if (inOverlapZone) {
        head.classList.remove('is-scrolled');
        head.classList.add('is-hidden');
      } else {
        head.classList.remove('is-scrolled', 'is-hidden');
      }
    }

    let nearTopTimeoutId: ReturnType<typeof setTimeout> | null = null;
    function onScroll() {
      updateHeaderState();
      const c = pageRef.current;
      if (!c) return;
      if (c.scrollTop <= SCROLL_TOP_NEAR) {
        if (nearTopTimeoutId) clearTimeout(nearTopTimeoutId);
        nearTopTimeoutId = setTimeout(updateHeaderState, SYNC_AFTER_STOP_MS);
      }
    }

    updateHeaderState();
    const rafId = requestAnimationFrame(updateHeaderState);
    container.addEventListener('scroll', onScroll, { passive: true });
    const io = new IntersectionObserver(updateHeaderState, { root: container, rootMargin: '0px', threshold: 0 });
    io.observe(hero);
    return () => {
      cancelAnimationFrame(rafId);
      if (nearTopTimeoutId) clearTimeout(nearTopTimeoutId);
      container.removeEventListener('scroll', onScroll);
      io.disconnect();
    };
  }, []);

  /* 区块进入视口时添加 in-view，触发淡入动效 */
  useEffect(() => {
    if (user) return;
    const elements: HTMLElement[] = [];
    if (heroRef.current) elements.push(heroRef.current);
    sectionRefs.current.forEach((el) => el && elements.push(el));
    if (ctaSectionRef.current) elements.push(ctaSectionRef.current);

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(VIEWPORT_CLASS);
        }
      });
    }, IO_OPTIONS);

    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [user]);

  /* 仅已登录时显示 spinner 并等待跳转；未登录时直接渲染首屏，不阻塞图片加载 */
  if (user) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '2rem',
        }}
      >
        <LoadingSpinner size="large" showText={false} />
      </div>
    );
  }

  return (
    <div className="home-page" ref={pageRef}>
      <MarketingHeader atHeroPage />

      <section className="home-hero in-view" ref={heroRef}>
        <video
          ref={heroVideoRef}
          className="home-hero-bg-img home-hero-video"
          aria-hidden
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_VIDEO_POSTER}
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
        </video>
        <div className="home-hero-overlay" aria-hidden />
        <div className="home-hero-hover-glow" aria-hidden />
        <div className="home-hero-mouse-glow" aria-hidden />
        <div className="home-hero-content">
          <h1 className="home-hero-title">让每一份才华都有价值</h1>
          <p className="home-hero-subtitle">你只管创作，其余的交给基音</p>
          <p className="home-hero-trust">创作、发行、收益与版权支持，一站式完成。</p>
          <div className="home-hero-buttons">
            <Link href={getCreatorLoginUrl()} className="home-cta-button">
              进入创作者平台
            </Link>
          </div>
        </div>
        <button
          type="button"
          className="home-hero-scroll-hint"
          aria-label="向下滚动"
          onClick={() => document.getElementById('home-section-1')?.scrollIntoView({ behavior: 'smooth' })}
        >
          <span className="home-hero-scroll-hint-icon" />
        </button>
      </section>

      {BLOCKS.map((block, index) => (
        <section
          key={block.id}
          id={index === 0 ? 'home-section-1' : undefined}
          className={`home-section home-section--fullbleed${index % 2 === 1 ? ' home-section--fullbleed-right' : ''}`}
          style={{
            backgroundImage: `url(/home/${block.imageSlug}.png)`,
            backgroundPosition: index % 2 === 1 ? 'left center' : 'right center',
          }}
          ref={(el) => {
            sectionRefs.current[index] = el;
          }}
          aria-label={block.alt}
        >
          <div className="home-section-fullbleed-content">
            <h2 className="home-block-title">{block.title}</h2>
            <p className="home-block-tagline">{block.tagline}</p>
            <p className="home-block-text">{block.text}</p>
          </div>
        </section>
      ))}

      <section className="home-cta-section" ref={ctaSectionRef}>
        <div className="home-cta-section-inner">
          <h2 className="home-cta-section-title">加入基音，开启创作之旅</h2>
          <p className="home-cta-section-sub">作品直达 30+ 全球平台 · 收益透明 · 保留 100% 版权</p>
          <div className="home-cta-section-buttons">
            <Link href={getCreatorRegisterUrl()} className="home-cta-button">
              免费注册
            </Link>
            <Link href={getCreatorLoginUrl()} className="home-cta-button">
              进入创作者平台
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
